import React, { useState, useEffect } from 'react';
import { api } from '../services/apiClient';
import { Eye, TrendingUp, TrendingDown, ExternalLink, Search, ShieldAlert, RefreshCw, X } from 'lucide-react';
import ProPaywall from '../components/ProPaywall'; // Import du Paywall

export default function InsiderRadarPage() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // NOUVEAU : État pour le statut PRO
    const [isProUser, setIsProUser] = useState(false);
    
    // Filtres Locaux
    const [filterBuyOnly, setFilterBuyOnly] = useState(false);
    const [filterMassive, setFilterMassive] = useState(false);
    
    // Recherche Serveur
    const [searchInput, setSearchInput] = useState('');
    const [activeSymbol, setActiveSymbol] = useState('');

    const fetchInsiders = async (symbol = '') => {
        setLoading(true);
        try {
            const url = symbol ? `/insiders/latest?symbol=${symbol}&limit=200` : '/insiders/latest?limit=100';
            const res = await api.get(url);
            setTransactions(res.data);
            setActiveSymbol(symbol);
        } catch (err) {
            console.error("Erreur de chargement des initiés", err);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    // Chargement initial (Flux global)
    useEffect(() => {
        const fetchUserDataAndInsiders = async () => {
             try {
                // Vérification du statut PRO
                const token = localStorage.getItem('token');
                if (token) {
                    const userRes = await api.get('/auth/me');
                    setIsProUser(userRes.data.is_pro);
                }
            } catch (error) {
                console.error("Erreur Auth:", error);
            }
            
            fetchInsiders();
        };
        fetchUserDataAndInsiders();
    }, []);

    // Déclenchement de la recherche
    const handleSearch = (e) => {
        e.preventDefault();
        fetchInsiders(searchInput.trim().toUpperCase());
    };

    // Bouton Reset
    const handleReset = () => {
        setSearchInput('');
        fetchInsiders('');
    };

    // Application des filtres locaux
    const filteredTransactions = transactions.filter(t => {
        if (filterBuyOnly && !t.is_buy) return false;
        if (filterMassive && t.total_value < 500000) return false;
        return true;
    });

    const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* HEADER - Toujours visible pour donner envie */}
                <div className="bg-slate-900 rounded-3xl p-8 mb-8 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
                    <div className="absolute right-0 top-0 w-64 h-64 bg-indigo-500 rounded-full blur-[80px] opacity-20"></div>
                    <div className="relative z-10 flex items-center gap-4">
                        <div className="w-16 h-16 bg-indigo-500/20 rounded-2xl flex items-center justify-center text-indigo-400 border border-indigo-500/30 shrink-0">
                            <Eye size={32} />
                        </div>
                        <div>
                            <h1 className="text-3xl font-black">Radar des Initiés</h1>
                            <p className="text-indigo-200">Traquez les achats et ventes des dirigeants (SEC Form 4) en temps réel.</p>
                        </div>
                    </div>
                </div>

                {/* CONDITION MAGIQUE : Si pas PRO => Paywall, Sinon => Tableau et Filtres */}
                {!isProUser ? (
                    <ProPaywall 
                        title="Radar des Initiés PRO" 
                        description="Analysez les déclarations SEC Form 4 en direct. Suivez les dirigeants qui achètent des actions de leur propre entreprise et détectez les mouvements massifs." 
                    />
                ) : (
                    <div className="animate-in fade-in duration-500">
                        {/* BARRE DE RECHERCHE SERVEUR ET FILTRES LOCAUX */}
                        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
                            
                            <form onSubmit={handleSearch} className="relative w-full md:w-96 flex gap-2">
                                <div className="relative w-full">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input 
                                        type="text" 
                                        placeholder="Chercher un Ticker (ex: AAPL, TSLA)..." 
                                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all uppercase" 
                                        value={searchInput} 
                                        onChange={e => setSearchInput(e.target.value)} 
                                        disabled={loading}
                                    />
                                </div>
                                <button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 rounded-xl font-bold transition-colors shrink-0">
                                    {loading ? <RefreshCw size={18} className="animate-spin" /> : <Search size={18} />}
                                </button>
                            </form>

                            <div className="flex gap-3 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                                {activeSymbol && (
                                    <button onClick={handleReset} className="flex-none px-5 py-3 rounded-xl text-sm font-black border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-2">
                                        <X size={16} className="text-rose-500" /> Effacer {activeSymbol}
                                    </button>
                                )}
                                <button onClick={() => setFilterBuyOnly(!filterBuyOnly)} className={`flex-none px-5 py-3 rounded-xl text-sm font-black border transition-colors flex items-center gap-2 ${filterBuyOnly ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                    <TrendingUp size={16} /> Achats Uniquement
                                </button>
                                <button onClick={() => setFilterMassive(!filterMassive)} className={`flex-none px-5 py-3 rounded-xl text-sm font-black border transition-colors flex items-center gap-2 ${filterMassive ? 'bg-amber-50 border-amber-200 text-amber-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                    <ShieldAlert size={16}/> Massifs {'>'} 500k$
                                </button>
                            </div>
                        </div>

                        {/* MESSAGE DE CONTEXTE SI RECHERCHE ACTIVE */}
                        {activeSymbol && !loading && (
                            <div className="mb-4 text-sm font-bold text-slate-500 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                                Affichage de l'historique complet des initiés pour <span className="text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">{activeSymbol}</span>
                            </div>
                        )}

                        {/* TABLEAU */}
                        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm whitespace-nowrap">
                                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                                        <tr>
                                            <th className="px-6 py-5 font-black uppercase tracking-wider text-xs">Date</th>
                                            <th className="px-6 py-5 font-black uppercase tracking-wider text-xs">Entreprise</th>
                                            <th className="px-6 py-5 font-black uppercase tracking-wider text-xs">Dirigeant / Rôle</th>
                                            <th className="px-6 py-5 font-black uppercase tracking-wider text-xs text-center">Type</th>
                                            <th className="px-6 py-5 font-black uppercase tracking-wider text-xs text-right">Montant</th>
                                            <th className="px-6 py-5 font-black uppercase tracking-wider text-xs text-center">SEC</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {loading ? (
                                            <tr><td colSpan="6" className="px-6 py-16 text-center text-slate-400 font-bold flex flex-col items-center justify-center gap-4"><RefreshCw size={32} className="animate-spin text-indigo-500"/> Analyse des archives de la SEC en cours...</td></tr>
                                        ) : filteredTransactions.length === 0 ? (
                                            <tr><td colSpan="6" className="px-6 py-16 text-center text-slate-400 font-bold bg-slate-50/50">Aucune transaction trouvée pour ces critères.</td></tr>
                                        ) : (
                                            filteredTransactions.map((t, idx) => (
                                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4 text-slate-500 font-medium">{t.transaction_date.split(' ')[0]}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center font-black text-white text-xs shadow-sm">{t.symbol}</div>
                                                            <span className="font-bold text-slate-900 text-base">{t.symbol}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <p className="font-black text-slate-900">{t.reporting_name}</p>
                                                        <p className="text-xs font-bold text-slate-400 truncate max-w-[200px]" title={t.owner_type}>{t.owner_type}</p>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        {t.is_buy ? (
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-black uppercase tracking-wider"><TrendingUp size={14} strokeWidth={3}/> Achat</span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-100 text-rose-700 text-xs font-black uppercase tracking-wider"><TrendingDown size={14} strokeWidth={3}/> Vente</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <p className={`font-black text-base ${t.is_buy ? 'text-emerald-600' : 'text-slate-900'}`}>{formatCurrency(t.total_value)}</p>
                                                        <p className="text-xs font-bold text-slate-400 mt-0.5">{t.shares.toLocaleString()} actions à {formatCurrency(t.price)}</p>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        {t.link ? (
                                                            <a href={t.link} target="_blank" rel="noopener noreferrer" className="inline-flex p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors" title="Voir le document officiel SEC Form 4">
                                                                <ExternalLink size={20} />
                                                            </a>
                                                        ) : (
                                                            <span className="text-slate-300">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
